import moment from 'moment';

import { isEmpty, isEmptyArray, isNullOrUndefined } from './index';

// 👉 Required Validator
export const requiredValidator = (value: unknown) => {
  if (isNullOrUndefined(value) || isEmptyArray(value) || value === false) return 'This field is required';

  return !!String(value).trim().length || 'This field is required';
};

export const requiredValidatorWithNumber = (value: unknown) => {
  if (isNullOrUndefined(value) || isEmptyArray(value) || value === false || value === 0)
    return 'This field is required';

  return !!String(value).trim().length || 'This field is required';
};

export const requiredIfValidator = (condition: unknown, value: unknown) => {
  if (condition) {
    if (isNullOrUndefined(value) || isEmptyArray(value) || value === false || value === '')
      return 'This field is required';

    return !!String(value).trim().length || 'This field is required';
  }
};

export const requiredIfCondition = (condition: unknown) => {
  return condition ? 'This field is required' : true;
};

export const minThreeAlphabetsValidator = (value: string) => {
  if (!value) return true;

  const totalLength = value.trim().length;
  if (totalLength === 0) return true;

  const letterCount = (value.match(/[a-zA-Z]/g) || []).length;
  const halfLength = Math.ceil(totalLength / 2);

  return letterCount >= halfLength || `Must contain at least ${halfLength} alphabets`;
};

// 👉 Email Validator
export const emailValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  if (Array.isArray(value)) return value.every(val => re.test(String(val))) || 'The Email field must be a valid email';

  return re.test(String(value)) || 'The Email field must be a valid email';
};

// 👉 Password Validator
export const passwordValidator = (password: string) => {
  const regExp = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/;

  const validPassword = regExp.test(password);

  return (
    validPassword ||
    'Field must contain at least one uppercase, lowercase, special character and digit with min 8 chars'
  );
};

// 👉 Confirm Password Validator
/** Rejects leading, trailing, or internal whitespace (spaces, tabs, etc.). */
export const passwordNoWhitespaceValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  return !/\s/.test(String(value)) || 'Password must not contain spaces';
};

export const confirmedValidator = (value: string, target: string) =>
  value === target || 'The entered passwords do not match';

// 👉 Between Validator
export const betweenValidator = (value: unknown, min: number, max: number) => {
  const valueAsNumber = Number(value);

  return (Number(min) <= valueAsNumber && Number(max) >= valueAsNumber) || `Enter number between ${min} and ${max}`;
};

// 👉 Integer Validator
export const integerValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  if (Array.isArray(value))
    return value.every(val => /^-?[0-9]+$/.test(String(val))) || 'This field must be an integer';

  return /^-?[0-9]+$/.test(String(value)) || 'This field must be an integer';
};

// 👉 Regex Validator
export const regexValidator = (value: unknown, regex: RegExp | string): string | boolean => {
  if (isEmpty(value)) return true;

  let regeX = regex;
  if (typeof regeX === 'string') regeX = new RegExp(regeX);

  if (Array.isArray(value)) return value.every(val => regexValidator(val, regeX));

  return regeX.test(String(value)) || 'The Regex field format is invalid';
};

// 👉 Alpha Validator
export const alphaValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  return /^[A-Z]*$/i.test(String(value)) || 'The Alpha field may only contain alphabetic characters';
};

// 👉 URL Validator
export const urlValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;

  return re.test(String(value)) || 'URL is invalid';
};

// 👉 Length Validator
export const lengthValidator = (value: unknown, length: number) => {
  if (isEmpty(value)) return true;

  return String(value).length === length || `The Min Character field must be at least ${length} characters`;
};

// 👉 Max Length Validator
export const maxLengthValidator = (value: unknown, length: number) => {
  if (isEmpty(value)) return true;

  return String(value).length <= length || `The maximum number of characters allowed is ${length}`;
};

// 👉 Alpha-dash Validator
export const alphaDashValidator = (value: unknown) => {
  if (isEmpty(value)) return true;

  const valueAsString = String(value);

  return /^[0-9A-Z_-]*$/i.test(valueAsString) || 'All Character are not valid';
};

// 👉 Duplicate Option Validator
export const duplicateOptionValidator = (options: string[], currentIndex: number): string | true => {
  const current = options[currentIndex]?.trim().toLowerCase();

  return options.some((opt, i) => i !== currentIndex && opt?.trim().toLowerCase() === current)
    ? 'Duplicate option value'
    : true;
};

// 👉 Numeric Validator
export const minValueValidator = (value: unknown, min = 0) => {
  return (!isNaN(Number(value)) && Number(value) >= min) || 'This field must be an integer';
};

// 👉 Required Carer Age
export const requiredCarerAgeValidator = (date: unknown) => {
  if (!date) return 'This field is required';

  const age = moment().diff(moment(date, 'YYYY-MM-DD'), 'years');

  return age >= 16 ? true : 'Age must be at least 16 years old';
};

// 👉 Required Client Age
export const requiredClientAgeValidator = (date: unknown) => {
  if (!date) return 'This field is required';

  const age = moment().diff(moment(date, 'YYYY-MM-DD'), 'years');

  return age >= 12 ? true : 'Age must be at least 12 years old';
};
